import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined, SearchOutlined, ReloadOutlined, FilterOutlined, InfoCircleOutlined } from '@ant-design/icons';
import StackCard from './components/StackCard';
import EmptyState from '../../components/shared/EmptyState/EmptyState';
import SideModal from '../../components/shared/SideModal/SideModal';

interface Stack {
  id: string;
  name: string;
  type: string;
  status: 'running' | 'stopped' | 'deploying' | 'error';
  createdAt: string;
  region: string;
}

const Stacks: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'running' | 'stopped' | 'deploying' | 'error' | 'all'>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Mock data - Stacks
  const [stacks] = useState<Stack[]>([
    {
      id: '1',
      name: 'LAMP Production',
      type: 'LAMP',
      status: 'running',
      createdAt: '2 days ago',
      region: 'us-east-1',
    },
    {
      id: '2',
      name: 'Node.js Development',
      type: 'NODEJS',
      status: 'running',
      createdAt: '1 week ago',
      region: 'us-west-2',
    },
    {
      id: '3',
      name: 'MEAN Stack',
      type: 'MEAN',
      status: 'deploying',
      createdAt: 'Just now',
      region: 'eu-west-1',
    },
    {
      id: '4',
      name: 'Django API',
      type: 'Django',
      status: 'stopped',
      createdAt: '3 days ago',
      region: 'us-east-1',
    },
    {
      id: '5',
      name: 'Nginx Proxy',
      type: 'Nginx',
      status: 'running',
      createdAt: '5 hours ago',
      region: 'ap-southeast-1',
    },
  ]);

  const filteredStacks = stacks.filter((stack) => {
    const matchesSearch = 
      stack.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      stack.type.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || stack.status === filterStatus;
    const matchesType = filterType === 'all' || stack.type === filterType;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const stackTypes = Array.from(new Set(stacks.map(s => s.type)));

  return (
    <div className="max-w-[1600px] mx-auto px-8 py-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">App Server</h1>
              <button
                onClick={() => setShowHelpModal(true)}
                className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                title="Get help about App Servers"
              >
                <InfoCircleOutlined className="text-lg" />
              </button>
            </div>
            <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">Pre-Configured Application Stacks</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 max-w-2xl">
              Deploy ready-to-use application server stacks with pre-configured environments. Choose from LAMP, Node.js, MEAN, Django, or Nginx stacks to quickly launch your applications.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <ReloadOutlined />
              Refresh
            </button>
            <button
              onClick={() => navigate('/stacks/create')}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <PlusOutlined />
              New App Server
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <SearchOutlined className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-800 dark:text-white"
            />
          </div>

          {/* Type Filter */}
          <div className="flex items-center gap-2">
            <FilterOutlined className="text-gray-400" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-800 dark:text-white"
            >
              <option value="all">All Types</option>
              {stackTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as typeof filterStatus)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-800 dark:text-white"
          >
            <option value="all">All Status</option>
            <option value="running">Running</option>
            <option value="stopped">Stopped</option>
            <option value="deploying">Deploying</option>
            <option value="error">Error</option>
          </select>

          {/* View Mode */}
          <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 text-sm ${
                viewMode === 'grid' ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 text-sm border-l border-gray-300 dark:border-gray-600 ${
                viewMode === 'list' ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Stacks Grid/List */}
      {filteredStacks.length === 0 ? (
        <EmptyState
          title="No app servers found"
          description={searchTerm || filterStatus !== 'all' || filterType !== 'all' 
            ? "Try adjusting your search or filters to find what you're looking for."
            : "Get started by deploying your first pre-configured stack."}
          action={!searchTerm && filterStatus === 'all' && filterType === 'all' ? {
            label: 'Create New App Server',
            onClick: () => navigate('/stacks/create')
          } : undefined}
        />
      ) : (
        <>
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredStacks.map((stack) => (
                <StackCard
                  key={stack.id}
                  name={stack.name}
                  type={stack.type}
                  status={stack.status}
                  createdAt={stack.createdAt}
                  region={stack.region}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredStacks.map((stack) => (
                <div key={stack.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
                  <StackCard
                    name={stack.name}
                    type={stack.type}
                    status={stack.status}
                    createdAt={stack.createdAt}
                    region={stack.region}
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Summary Stats */}
      {filteredStacks.length > 0 && (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total App Servers</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{stacks.length}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Running</div>
            <div className="text-2xl font-bold text-green-600">
              {stacks.filter(s => s.status === 'running').length}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Deploying</div>
            <div className="text-2xl font-bold text-blue-600">
              {stacks.filter(s => s.status === 'deploying').length}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Stopped</div>
            <div className="text-2xl font-bold text-gray-600">
              {stacks.filter(s => s.status === 'stopped').length}
            </div>
          </div>
        </div>
      )}

      {/* Help Modal */}
      <SideModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        title="About App Servers"
      >
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">What are App Servers?</h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              App Servers are pre-configured application stacks that provide ready-to-use environments for your applications. 
              Instead of manually setting up each component, you can deploy a complete stack with all necessary software 
              pre-installed and configured.
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Available Stacks</h3>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
              <li><strong>LAMP Stack:</strong> Linux, Apache, MySQL, PHP - Perfect for traditional web applications and WordPress</li>
              <li><strong>Node.js Runtime:</strong> JavaScript runtime environment for scalable network applications</li>
              <li><strong>MEAN Stack:</strong> MongoDB, Express.js, Angular, Node.js - Full-stack JavaScript solution</li>
              <li><strong>Django Stack:</strong> Python web framework with PostgreSQL and Nginx</li>
              <li><strong>Nginx Web Server:</strong> High-performance web server and reverse proxy</li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Benefits</h3>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
              <li>Quick deployment - Get your stack running in minutes</li>
              <li>Pre-configured - All components are optimized and ready to use</li>
              <li>Scalable - Easily adjust resources as your needs grow</li>
              <li>Managed - Automatic updates and security patches</li>
              <li>Flexible - Choose instance types and storage based on your requirements</li>
            </ul>
          </div>
        </div>
      </SideModal>
    </div>
  );
};

export default Stacks;

